/*
 * Purpose: Apply Gmail inbound + merge LLM/rule plans into final OutcomePlan
 * Last modified: 2026-06-25 | Agent
 * Completeness: 90
 */
import { planFromLlm } from './llmPlanner';
import { planFromOutcome } from './outcomePlanner';
import { createGmailInbox, checkWebhookServerHealth } from '@/services/connectors/gmailInbound';
import { gmailWebhookPostUrl } from '@/lib/apiBase';
import {
  DEFAULT_JOB_GMAIL_QUERY,
  defaultGmailSourceForOutcome,
  type GmailInboundSource,
} from '@/services/connectors/gmailSource';
import { buildConnectorsForOutcome } from '@/services/connectors/library/connectorFactory';
import { resolveConnectorsForPlan } from '@/services/connectors/library/connectorResolver';
import type { OutcomePlan } from './types';

const genId = () => `plan-${Date.now().toString(36)}`;

function needsGmail(outcome: string, plan: OutcomePlan): boolean {
  if (plan.useGmailInbound) return true;
  const lower = outcome.toLowerCase();
  return (
    /\b(email|gmail|inbox|linkedin.*email|job alert|receipt)\b/i.test(lower) ||
    plan.connectors.some((c) => c.id.includes('gmail'))
  );
}

async function wireGmailInbound(plan: OutcomePlan): Promise<OutcomePlan> {
  const source: GmailInboundSource = defaultGmailSourceForOutcome(
    plan.rawOutcome,
    plan.interpretedGoal
  );
  const serverUp = await checkWebhookServerHealth();
  let inboxId = plan.gmailInboxId;
  let postUrl = plan.gmailInboxId ? gmailWebhookPostUrl(plan.gmailInboxId) : undefined;

  // Edge inbox is only for webhook mode — not required for OAuth reads of your real Gmail.
  if (source === 'webhook' && serverUp && !inboxId) {
    try {
      const inbox = await createGmailInbox();
      inboxId = inbox.inboxId;
      postUrl = inbox.postUrl;
    } catch {
      /* keep plan without inbox */
    }
  }

  const steps = plan.steps.map((s, i) => {
    if (i === 0 && s.type === 'trigger' && (s.subtype === 'webhook' || s.subtype === 'gmail_inbound')) {
      return {
        ...s,
        subtype: 'gmail_inbound',
        blockId: 'trigger.gmail_inbound',
        label: s.label || 'Gmail email received',
        rationale:
          source === 'gmail_api'
            ? 'Reads your existing Gmail via OAuth — filter with a search query (no separate inbox).'
            : 'AutoBlocks edge inbox — external system POSTs parsed email JSON to webhook URL.',
        config: {
          ...s.config,
          connectorId: 'ab.gmail',
          name: s.config.name || 'Gmail email received',
          inboxId: source === 'webhook' ? inboxId || 'pending-inbox' : undefined,
          postUrl: source === 'webhook' ? postUrl || '/api/webhooks/gmail/{inboxId}' : undefined,
          source,
          gmailQuery: DEFAULT_JOB_GMAIL_QUERY,
        },
      };
    }
    return s;
  });

  const assumptions = [...plan.assumptions];
  if (source === 'gmail_api') {
    assumptions.push(
      'Gmail API mode: Connect Gmail in compose review, then run — searches your real inbox (e.g. LinkedIn job alerts).'
    );
  } else if (serverUp && inboxId) {
    assumptions.push(`Edge inbox ready at ${postUrl} — forward parsed email JSON here (optional path).`);
  } else {
    assumptions.push('For edge inbox: start npm run dev or deploy inbound Worker.');
  }

  return {
    ...plan,
    steps,
    assumptions,
    gmailInboxId: source === 'webhook' ? inboxId : undefined,
    gmailPostUrl: source === 'webhook' ? postUrl : undefined,
    useGmailInbound: true,
  };
}

export async function planFromOutcomeAsync(
  rawOutcome: string,
  options?: {
    missionKnowledgeContext?: string;
    missionDossierContext?: string;
    mapContext?: string;
  }
): Promise<OutcomePlan> {
  const trimmed = rawOutcome.trim();
  if (!trimmed) {
    throw new Error('Describe the outcome you want — e.g. "When LinkedIn emails me jobs, track applications I make."');
  }

  let plan: OutcomePlan | null = await planFromLlm(trimmed, options);

  if (!plan) {
    plan = { ...planFromOutcome(trimmed), plannerSource: 'rules' };
  }

  const { matchedTemplateId: _ignored, ...goalPlan } = plan;

  if (needsGmail(trimmed, goalPlan)) {
    plan = await wireGmailInbound(goalPlan);
  } else {
    plan = goalPlan;
  }

  const factory = await buildConnectorsForOutcome(trimmed, plan.interpretedGoal);
  const resolvedConnectors = resolveConnectorsForPlan(plan, factory);

  return {
    ...plan,
    id: plan.id || genId(),
    resolvedConnectors,
    builtConnectors: factory.builtConnectors,
    connectorFactoryNote: factory.rationale,
  };
}
